import React from 'react';
import orderBy from 'lodash.orderby';
import partition from 'lodash.partition';

import { SectionResumoType } from '@/features/enterprise/models/types';

import { ArtifactType, Solicitacao } from '@/models/Solicitacao';
import { useExecucaoContext } from '../providers/ExecucaoProvider';
import { useSolicitacoesConsolidadas } from './useSolicitacoesConsolidadas';

type SectionsResumo = [
  SectionResumoType[],
  SectionResumoType[],
  SectionResumoType[],
];

function hasAnySolicitacaoByTipo(
  solicitacoes: Solicitacao[],
  tipo: ArtifactType,
): boolean {
  return solicitacoes.some(s => s.tipo === tipo);
}

export function renderableSections(
  sumario: SectionResumoType[],
  solicitacoes: Solicitacao[],
): SectionResumoType[] {
  return orderBy(sumario, ['ordem']).filter(item => {
    if (item.modelo === 'solicitacoes-exame') {
      return hasAnySolicitacaoByTipo(solicitacoes, 'solicitacao-exame');
    }

    if (item.modelo === 'solicitacoes-medicamento') {
      return hasAnySolicitacaoByTipo(solicitacoes, 'prescricao-medica');
    }

    if (item.modelo === 'solicitacoes-procedimento') {
      return hasAnySolicitacaoByTipo(solicitacoes, 'procedimento');
    }

    return item.status === 'completed';
  });
}

export function splitSumarioSections(
  sumario: SectionResumoType[],
  solicitacoes: Solicitacao[],
): SectionsResumo {
  const sorted = renderableSections(sumario, solicitacoes);

  const [withDisplayBlock, _] = partition<SectionResumoType>(
    sorted,
    item => item.display_block,
  );

  const splitIndex = Math.ceil(_.length / 2);
  const firstHalf = _.slice(0, splitIndex);
  const secondHalf = _.slice(splitIndex, _.length);

  return [withDisplayBlock, firstHalf, secondHalf];
}

export default function useSumarioSections(): SectionsResumo {
  const [{ protocoloExecutado }] = useExecucaoContext();
  const { solicitacoes } = useSolicitacoesConsolidadas();

  const sections = React.useMemo<SectionsResumo>(() => {
    if (protocoloExecutado?.sumario) {
      return splitSumarioSections(protocoloExecutado.sumario, solicitacoes);
    }

    return [[], [], []];
  }, [solicitacoes, protocoloExecutado]);

  return sections;
}
